// ===================================================
// Регистрация через Маркбэйс id (auth.markbase.ru)
// ===================================================
import React, { useEffect, useRef, useCallback } from 'react';
import { UserPlus, ShieldCheck } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { checkAuth } from '../../store/authSlice';
import AuthLayout from './AuthLayout';
import UnifiedAccountButton from '../../components/Auth/UnifiedAccountButton';
import { AUTH_MESSAGE_TYPE } from './AuthCallbackPage';
import {
  AUTH_BROADCAST_CHANNEL,
  AUTH_POPUP_LOGIN_NAME,
  AUTH_POPUP_REGISTER_NAME,
  closePopupRef,
} from './authPopup';
import {
  isSafeReturnUrl,
  buildRedirectTarget as buildRedirectTargetShared,
  resolvePostAuthTarget as resolvePostAuthTargetShared,
} from './postAuthRedirect';

const getUamUrl = () => {
  const raw = process.env.REACT_APP_UAM_URL || 'https://auth.markbase.ru';
  return raw.replace(/\/+$/, '');
};

const POPUP_WIDTH = 460;
const POPUP_HEIGHT = 700;

export default function Register() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, checkingAuth, user, company } = useSelector((s) => s.auth);
  const hasNavigatedRef = useRef(false);
  const popupRef = useRef(null);
  const popupCheckIntervalRef = useRef(null);

  const returnUrl = searchParams.get('return_url') || '';
  const toPath = returnUrl && isSafeReturnUrl(returnUrl)
    ? (returnUrl.startsWith('http') ? returnUrl : returnUrl)
    : '/dashboard';

  const buildRedirectTarget = useCallback((path) => {
    return buildRedirectTargetShared(path || toPath);
  }, [toPath]);

  const resolvePostAuthTarget = useCallback((authState, requestedTarget, flow = 'register') => {
    const mergedState = {
      user: authState?.user || user,
      company: authState?.company || company || null,
    };
    return resolvePostAuthTargetShared(mergedState, requestedTarget || buildRedirectTarget(), flow);
  }, [buildRedirectTarget, user, company]);

  const doRedirect = useCallback((target) => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }
    closePopupRef(popupRef);
    window.location.href = target || buildRedirectTarget();
  }, [buildRedirectTarget]);

  // Проверка авторизации и редиректа после закрытия popup (auth теперь по cookie uam_session)
  const checkAuthAndRedirect = useCallback(async (flow = 'register', requestedTarget = null) => {
    if (hasNavigatedRef.current) return;

    const delays = [300, 800, 1500, 2500, 4000];
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (hasNavigatedRef.current) return;
      try {
        const result = await dispatch(checkAuth()).unwrap();
        if (result && result.isAuthenticated) {
          doRedirect(resolvePostAuthTarget(result, requestedTarget || buildRedirectTarget(), flow));
          return;
        }
      } catch (error) {
        console.warn(`Auth check attempt ${attempt + 1}/${delays.length} failed:`, error);
      }
      if (attempt < delays.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
      }
    }
    console.warn('Все попытки проверки авторизации после закрытия окна не удались');
  }, [dispatch, doRedirect, buildRedirectTarget, resolvePostAuthTarget]);

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  useEffect(() => {
    if (!checkingAuth && (isAuthenticated || user) && !hasNavigatedRef.current) {
      doRedirect(resolvePostAuthTarget({ user, company }, buildRedirectTarget(), 'register'));
    }
  }, [checkingAuth, isAuthenticated, user, company, doRedirect, buildRedirectTarget, resolvePostAuthTarget]);

  useEffect(() => {
    const onAuthSuccess = async (data) => {
      if (hasNavigatedRef.current) return;
      if (data?.type !== AUTH_MESSAGE_TYPE) return;
      try {
        const authState = await dispatch(checkAuth()).unwrap();
        if (!authState?.isAuthenticated) return;
        const target = resolvePostAuthTarget(
          authState,
          buildRedirectTarget(data.to || '/dashboard'),
          data.flow || 'register',
        );
        doRedirect(target);
      } catch (error) {
        console.warn('Проверка авторизации после callback не удалась:', error);
      }
    };
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      onAuthSuccess(event.data);
    };
    window.addEventListener('message', handler);
    let channel = null;
    try {
      channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      channel.onmessage = (event) => onAuthSuccess(event.data);
    } catch (_) { /* ignore */ }
    return () => {
      window.removeEventListener('message', handler);
      try { channel?.close(); } catch (_) { /* ignore */ }
    };
  }, [dispatch, doRedirect, buildRedirectTarget, resolvePostAuthTarget]);

  const openRegisterPopup = useCallback(() => {
    const uam = getUamUrl();
    const callbackTo = `${window.location.origin}/auth/callback?to=${encodeURIComponent(toPath)}`;
    const url = `${uam}/register?return_url=${encodeURIComponent(callbackTo)}`;
    const left = Math.round((window.screen.width - POPUP_WIDTH) / 2);
    const top = Math.round((window.screen.height - POPUP_HEIGHT) / 2);
    const opts = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;

    popupRef.current = window.open(url, AUTH_POPUP_REGISTER_NAME, opts);

    // Отслеживание закрытия popup и автоматический редирект после регистрации
    if (popupRef.current) {
      const checkPopupClosed = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(checkPopupClosed);
          popupCheckIntervalRef.current = null;
          // Проверяем авторизацию и делаем редирект
          checkAuthAndRedirect();
          popupRef.current = null;
        }
      }, 500);

      popupCheckIntervalRef.current = checkPopupClosed;

      // Очистка интервала через 5 минут (на случай, если popup не закроется)
      setTimeout(() => {
        if (popupCheckIntervalRef.current === checkPopupClosed) {
          clearInterval(checkPopupClosed);
          popupCheckIntervalRef.current = null;
        }
      }, 5 * 60 * 1000);
    }
  }, [toPath, checkAuthAndRedirect]);

  const openLoginPopup = useCallback(() => {
    const uam = getUamUrl();
    const callbackTo = `${window.location.origin}/auth/callback?to=${encodeURIComponent(toPath)}`;
    const url = `${uam}/login?return_url=${encodeURIComponent(callbackTo)}`;
    const left = Math.round((window.screen.width - POPUP_WIDTH) / 2);
    const top = Math.round((window.screen.height - POPUP_HEIGHT) / 2);
    const opts = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;

    popupRef.current = window.open(url, AUTH_POPUP_LOGIN_NAME, opts);

    // Отслеживание закрытия popup и автоматический редирект после входа
    if (popupRef.current) {
      const checkPopupClosed = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(checkPopupClosed);
          popupCheckIntervalRef.current = null;
          // Проверяем авторизацию и делаем редирект
          checkAuthAndRedirect();
          popupRef.current = null;
        }
      }, 500);

      popupCheckIntervalRef.current = checkPopupClosed;

      // Очистка интервала через 5 минут (на случай, если popup не закроется)
      setTimeout(() => {
        if (popupCheckIntervalRef.current === checkPopupClosed) {
          clearInterval(checkPopupClosed);
          popupCheckIntervalRef.current = null;
        }
      }, 5 * 60 * 1000);
    }
  }, [toPath, checkAuthAndRedirect]);

  if (checkingAuth) {
    return (
      <div className="mb-register__boot" role="status" aria-live="polite">
        <div className="mb-spin mb-register__spinner" aria-hidden />
      </div>
    );
  }

  return (
    <AuthLayout>
      <div className="mb-register">
        <div className="mb-register__header">
          <div className="mb-register__badge">
            <ShieldCheck className="mb-register__badge-icon" size={18} aria-hidden />
            <span>Единый аккаунт</span>
          </div>
          <h3 className="mb-register__title">Регистрация в MarkBase</h3>
          <span className="mb-register__subtitle">
            Создайте единый аккаунт экосистемы
          </span>
        </div>

        <UnifiedAccountButton
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          className="mb-register__cta"
          onClick={openRegisterPopup}
          primaryLabel="Зарегистрироваться"
          secondaryLabel="Маркбэйс id"
        />

        <div className="mb-register__footer">
          <span className="mb-register__footer-label">Уже есть аккаунт? </span>
          <button
            type="button"
            className="mb-register__login-link mb-btn mb-btn--ghost"
            onClick={openLoginPopup}
          >
            Войти
          </button>
        </div>

        <div className="mb-register__hint">
          Откроется окно регистрации на auth.markbase.ru. Регистрация, капча и подтверждение email выполняются там. После регистрации вы вернётесь на эту страницу.
        </div>
      </div>
    </AuthLayout>
  );
}
